import { createSlice } from '@reduxjs/toolkit'
import { keyBy } from '../../../helpers/common'
import { formatCompanyInfoData } from '../../../helpers/companyInfo'
import { register, syncInitialState } from '../../../helpers/reducerRegistry'
import { DEFAULT_CODE } from '../../../layout/header/search/constants'
import { checkPreviousState } from '../../../layout/header/search/helpers'
import {
  getCompanyHeaderThunk,
  getDiscountOrder,
  getExchangeRatesThunk,
  getForeignRegionsThunk,
  getIcbThunk,
  getLocationThunk,
  getPricingPackagesByUserTypeThunk,
  getPricingPackagesThunk,
  getTimezoneThunk,
  getUserProfileThunk,
  getVAT,
  getVietnameseLocationThunk,
  getVietnameseProvincesThunk,
  getVsicThunk,
} from './thunk'

export const keyLoading = {
  LOCATION: 'location',
  VIETNAMESE_LOCATION: 'vietnameseLocation',
  VSIC: 'vsic',
  ICB: 'icb',
  TIMEZONE: 'timezone',
  PRICING_PACKAGES: 'PRICING_PACKAGES',
  PRICING_PACKAGES_BY_USER_TYPE: 'PRICING_PACKAGES_BY_USER_TYPE',
  EXCHANGE_RATES: 'EXCHANGE_RATES',
  COMPANY_HEADER: 'COMPANY_HEADER',
  USER_PROFILE: 'USER_PROFILE',
  VIETNAMESE_PROVINCES: 'vietnameseProvinces',
  FOREIGN_REGIONS: 'foreignRegions',
  VAT: 'vat',
  DISCOUNT_ORDER: 'DISCOUNT_ORDER',
}

const initialState = {
  loading: {
    [keyLoading.LOCATION]: false,
    [keyLoading.VIETNAMESE_LOCATION]: false,
    [keyLoading.VSIC]: false,
    [keyLoading.ICB]: false,
    [keyLoading.TIMEZONE]: false,
    [keyLoading.PRICING_PACKAGES]: false,
    [keyLoading.PRICING_PACKAGES_BY_USER_TYPE]: false,
    [keyLoading.EXCHANGE_RATES]: false,
    [keyLoading.COMPANY_HEADER]: false,
    [keyLoading.USER_PROFILE]: false,
    [keyLoading.VIETNAMESE_PROVINCES]: false,
    [keyLoading.FOREIGN_REGIONS]: false,
    [keyLoading.VAT]: false,
    [keyLoading.DISCOUNT_ORDER]: false,
  },
  locations: [],
  vietnameseLocations: [],
  vsics: [],
  vsicById: {},
  icbs: [],
  icbById: {},
  timezones: [],
  pricingPackages: [],
  pricingPackagesByUserType: [],
  exchangeRates: [],
  companyHeader: {},
  isRedirect404CompanyHeader: false,
  userProfile: {},
  vietnameseProvinces: [],
  foreignRegions: [],
  vat: 0,
  discountOrder: [],
  country: {},
}

const persistKeys = ['country']

export const slice = createSlice({
  name: 'category',
  initialState: syncInitialState({
    name: 'category',
    initialState,
    persistKeys,
    persistKeysDisableCheckLocation: persistKeys,
  }),
  reducers: {
    resetLocation(state) {
      state.locations = []
      state.loading[keyLoading.LOCATION] = false
    },
    resetVietnameseLocation(state) {
      state.vietnameseLocations = []
      state.loading[keyLoading.VIETNAMESE_LOCATION] = false
    },
    resetVsic(state) {
      state.vsics = []
      state.loading[keyLoading.VSIC] = false
    },
    resetIcb(state) {
      state.icbs = []
      state.loading[keyLoading.ICB] = false
    },
    resetTimezone(state) {
      state.timezones = []
      state.loading[keyLoading.TIMEZONE] = false
    },
    resetPricingPackages(state) {
      state.pricingPackages = []
      state.loading[keyLoading.PRICING_PACKAGES] = false
    },
    resetPricingPackagesByUserType(state) {
      state.pricingPackagesByUserType = []
      state.loading[keyLoading.PRICING_PACKAGES_BY_USER_TYPE] = false
    },
    resetExchangeRates(state) {
      state.exchangeRates = []
      state.loading[keyLoading.EXCHANGE_RATES] = false
    },
    resetCompanyHeader(state) {
      state.companyHeader = {}
      state.loading[keyLoading.COMPANY_HEADER] = false
    },
    resetUserProfile(state) {
      state.userProfile = {}
      state.loading[keyLoading.USER_PROFILE] = false
    },
    resetVietnameseProvinces(state) {
      state.vietnameseProvinces = []
      state.loading[keyLoading.VIETNAMESE_PROVINCES] = false
    },
    resetForeignRegions(state) {
      state.foreignRegions = []
      state.loading[keyLoading.FOREIGN_REGIONS] = false
    },
    changeCountry: (state, action) => {
      state.country = action.payload
    },
  },
  extraReducers: (builder) => {
    // get location
    builder.addCase(getLocationThunk.pending, (state) => {
      state.loading[keyLoading.LOCATION] = true
    })
    builder.addCase(getLocationThunk.fulfilled, (state, action) => {
      const locations = action.payload || []
      state.locations = locations
      const vietnam = locations.find((item) => item.code === DEFAULT_CODE) ?? {}

      state.country = checkPreviousState(state.country, vietnam)
      state.loading[keyLoading.LOCATION] = false
    })
    builder.addCase(getLocationThunk.rejected, (state) => {
      state.loading[keyLoading.LOCATION] = false
    })
    // get vietnamese location
    builder.addCase(getVietnameseLocationThunk.pending, (state) => {
      state.loading[keyLoading.VIETNAMESE_LOCATION] = true
    })
    builder.addCase(getVietnameseLocationThunk.fulfilled, (state, action) => {
      state.vietnameseLocations = action.payload
      state.loading[keyLoading.VIETNAMESE_LOCATION] = false
    })
    builder.addCase(getVietnameseLocationThunk.rejected, (state) => {
      state.loading[keyLoading.VIETNAMESE_LOCATION] = false
    })
    // get vsic
    builder.addCase(getVsicThunk.pending, (state) => {
      state.loading[keyLoading.VSIC] = true
    })
    builder.addCase(getVsicThunk.fulfilled, (state, action) => {
      state.vsics = action.payload
      state.vsicById = keyBy(action.payload, 'id')
      state.loading[keyLoading.VSIC] = false
    })
    builder.addCase(getVsicThunk.rejected, (state) => {
      state.loading[keyLoading.VSIC] = false
    })
    // get icb
    builder.addCase(getIcbThunk.pending, (state) => {
      state.loading[keyLoading.ICB] = true
    })
    builder.addCase(getIcbThunk.fulfilled, (state, action) => {
      state.icbs = action.payload
      state.icbById = keyBy(action.payload, 'id')
      state.loading[keyLoading.ICB] = false
    })
    builder.addCase(getIcbThunk.rejected, (state) => {
      state.loading[keyLoading.ICB] = false
    })
    // get timezone
    builder.addCase(getTimezoneThunk.pending, (state) => {
      state.loading[keyLoading.TIMEZONE] = true
    })
    builder.addCase(getTimezoneThunk.fulfilled, (state, action) => {
      state.timezones = action.payload
      state.loading[keyLoading.TIMEZONE] = false
    })
    builder.addCase(getTimezoneThunk.rejected, (state) => {
      state.loading[keyLoading.TIMEZONE] = false
    })
    // getPricingPackages
    builder.addCase(getPricingPackagesThunk.pending, (state) => {
      state.loading[keyLoading.PRICING_PACKAGES] = true
    })
    builder.addCase(getPricingPackagesThunk.fulfilled, (state, action) => {
      state.pricingPackages = action.payload
      state.loading[keyLoading.PRICING_PACKAGES] = false
    })
    builder.addCase(getPricingPackagesThunk.rejected, (state) => {
      state.loading[keyLoading.PRICING_PACKAGES] = false
    })
    // getPricingPackagesByUserType
    builder.addCase(getPricingPackagesByUserTypeThunk.pending, (state) => {
      state.loading[keyLoading.PRICING_PACKAGES_BY_USER_TYPE] = true
    })
    builder.addCase(
      getPricingPackagesByUserTypeThunk.fulfilled,
      (state, action) => {
        state.pricingPackagesByUserType = action.payload
        state.loading[keyLoading.PRICING_PACKAGES_BY_USER_TYPE] = false
      },
    )
    builder.addCase(getPricingPackagesByUserTypeThunk.rejected, (state) => {
      state.loading[keyLoading.PRICING_PACKAGES_BY_USER_TYPE] = false
    })
    // getExchangeRates
    builder.addCase(getExchangeRatesThunk.pending, (state) => {
      state.loading[keyLoading.EXCHANGE_RATES] = true
    })
    builder.addCase(getExchangeRatesThunk.fulfilled, (state, action) => {
      state.exchangeRates = action.payload
      state.loading[keyLoading.EXCHANGE_RATES] = false
    })
    builder.addCase(getExchangeRatesThunk.rejected, (state) => {
      state.loading[keyLoading.EXCHANGE_RATES] = false
    })
    //get VAT
    builder.addCase(getVAT.pending, (state) => {
      state.vat = 0
      state.loading[keyLoading.VAT] = true
    })
    builder.addCase(getVAT.fulfilled, (state, action) => {
      state.vat = action.payload
      state.loading[keyLoading.VAT] = false
    })
    builder.addCase(getVAT.rejected, (state) => {
      state.loading[keyLoading.VAT] = false
    })
    //get VAT
    builder.addCase(getDiscountOrder.pending, (state) => {
      state.discountOrder = {}
      state.loading[keyLoading.VAT] = true
    })
    builder.addCase(getDiscountOrder.fulfilled, (state, action) => {
      state.discountOrder = action.payload
      state.loading[keyLoading.DISCOUNT_ORDER] = false
    })
    builder.addCase(getDiscountOrder.rejected, (state) => {
      state.loading[keyLoading.DISCOUNT_ORDER] = false
    })
    // getCompanyHeaderThunk
    builder.addCase(getCompanyHeaderThunk.pending, (state) => {
      state.loading[keyLoading.COMPANY_HEADER] = true
      state.companyHeader = {}
    })
    builder.addCase(getCompanyHeaderThunk.fulfilled, (state, action) => {
      state.companyHeader = formatCompanyInfoData(action.payload)
      state.loading[keyLoading.COMPANY_HEADER] = false
    })
    builder.addCase(getCompanyHeaderThunk.rejected, (state, action) => {
      state.loading[keyLoading.COMPANY_HEADER] = false
      state.isRedirect404CompanyHeader = action.payload?.response?.status
    })
    // getUserProfile
    builder.addCase(getUserProfileThunk.pending, (state) => {
      state.loading[keyLoading.USER_PROFILE] = true
    })
    builder.addCase(getUserProfileThunk.fulfilled, (state, action) => {
      state.userProfile = {
        ...action.payload,
        email: action.payload?.companyEmail,
      }
      state.loading[keyLoading.USER_PROFILE] = false
    })
    builder.addCase(getUserProfileThunk.rejected, (state) => {
      state.loading[keyLoading.USER_PROFILE] = false
    })
    // get Vietnamese Provinces
    builder.addCase(getVietnameseProvincesThunk.pending, (state) => {
      state.loading[keyLoading.VIETNAMESE_PROVINCES] = true
    })
    builder.addCase(getVietnameseProvincesThunk.fulfilled, (state, action) => {
      state.vietnameseProvinces = action.payload
      state.loading[keyLoading.VIETNAMESE_PROVINCES] = false
    })
    builder.addCase(getVietnameseProvincesThunk.rejected, (state) => {
      state.loading[keyLoading.VIETNAMESE_PROVINCES] = false
    })
    // get Foreign Regions
    builder.addCase(getForeignRegionsThunk.pending, (state) => {
      state.loading[keyLoading.FOREIGN_REGIONS] = true
    })
    builder.addCase(getForeignRegionsThunk.fulfilled, (state, action) => {
      state.foreignRegions = action.payload
      state.loading[keyLoading.FOREIGN_REGIONS] = false
    })
    builder.addCase(getForeignRegionsThunk.rejected, (state) => {
      state.loading[keyLoading.FOREIGN_REGIONS] = false
    })
  },
})

export const selectLoading = (key) => (state) => state[slice.name].loading[key]

export const selectLocations = (state) => state[slice.name].locations

export const selectVietnameseLocations = (state) =>
  state[slice.name].vietnameseLocations

export const selectVsics = (state) => state[slice.name].vsics
export const selectVsicById = (id, attr) => (state) => {
  if (!id) return state[slice.name].vsicById
  const data = state[slice.name].vsicById[id]
  return data && attr ? data[attr] : data
}

export const selectIcbs = (state) => state[slice.name].icbs
export const selectIcbById = (id, attr) => (state) => {
  if (!id) return state[slice.name].icbById
  const data = state[slice.name].icbById[id]
  return data && attr ? data[attr] : data
}

export const selectTimezones = (state) => state[slice.name].timezones

export const selectPricingPackages = (state) =>
  state[slice.name].pricingPackages

export const selectPricingPackagesByUserType = (state) =>
  state[slice.name].pricingPackagesByUserType

export const selectExchangeRates = (state) => state[slice.name].exchangeRates

export const selectVAT = (state) => state[slice.name].vat

export const selectDiscountOrder = (state) => state[slice.name].discountOrder

export const selectCompanyHeader = (state) => state[slice.name].companyHeader
export const selectIsRedirect404CompanyHeader = (state) =>
  state[slice.name].isRedirect404CompanyHeader

export const selectUserProfile = (state) => state[slice.name].userProfile

export const selectVietnameseProvinces = (state) =>
  state[slice.name].vietnameseProvinces

export const selectForeignRegions = (state) => state[slice.name].foreignRegions
export const selectCountry = (state) => state[slice.name].country

export const {
  resetLocation,
  resetVietnameseLocation,
  resetVsic,
  resetIcb,
  resetTimezone,
  resetPricingPackages,
  resetPricingPackagesByUserType,
  resetExchangeRates,
  resetCompanyHeader,
  resetUserProfile,
  resetVietnameseProvinces,
  resetForeignRegions,
  changeCountry,
} = slice.actions

register('category', slice.reducer, persistKeys)
